/**
 * Service Details — single source of truth voor alle 12 SEO service-subpages.
 *
 * Elke entry beschrijft een dienst zoals hij in /diensten staat, maar dan
 * uitgebreid genoeg om als zelfstandige SEO landing-page te functioneren.
 *
 * Slug = URL pad zonder leading slash. Routes worden afgeleid in App.js.
 */

export const SERVICE_DETAILS = {
  'ai-seo': {
    slug: 'ai-seo',
    iconName: 'Search',
    accent: '#D4A017',
    h1: 'AI SEO — beter vindbaar in Google zonder maanden wachten',
    seoIntent: 'beter vindbaar in Google',
    metaTitle: 'AI SEO Bureau · Beter vindbaar in Google (automatisch)',
    metaDescription: 'AI voert uw SEO daadwerkelijk uit: technische audits, content-optimalisatie, schema en interne links. Auto-push voor WordPress/Shopify, snippets voor Wix.',
    keywords: 'AI SEO, SEO bureau, beter vindbaar in Google, SEO automatisch, WordPress SEO, Shopify SEO, technische SEO, AI SEO Nederland',
    hero: {
      tagline: 'AI-eerst SEO platform · Auto-push voor WordPress & Shopify',
      intro:
        'Vergeet maandelijkse SEO-rapporten die u eerst zelf moet doorvoeren. Onze AI scant uw site, vergelijkt met directe concurrenten en pusht de fixes direct via API (WordPress, Shopify, Webflow). Voor andere CMS\'en krijgt u kant-en-klare snippets of plaatst ons ops-team handmatig.',
    },
    body: {
      whyTitle: 'Waarom AI SEO werkt voor MKB',
      whyBlocks: [
        {
          title: 'Geen 6-week intake meer',
          desc: 'Klassieke SEO-bureaus starten met audits, plannen, decks. Onze AI begint binnen 60 seconden met daadwerkelijke fixes — geen workshop-cyclus.',
        },
        {
          title: 'Eerlijke statussen',
          desc: 'U ziet exact per opdracht: in wachtrij · wacht op koppeling · AI voert uit · uitgevoerd met live-URL. Géén nep-progress.',
        },
        {
          title: 'Werkt met élk CMS',
          desc: 'WordPress/Shopify/Webflow: API-push. Wix/Squarespace: snippet u plakt. Joomla/Drupal: ons ops-team plaatst binnen 5 werkdagen.',
        },
      ],
      whatTitle: 'Wat u krijgt — concreet',
      what: [
        'Volledige technische SEO-audit (titles, meta-descriptions, schema.org, sitemap, robots)',
        'Per-pagina content-audit (H1-structuur, woord-aantal, keyword density, leesbaarheid)',
        'Interne linkstructuur-analyse + voorstellen voor topic clusters',
        'Core Web Vitals scoring (LCP, INP, CLS) + concrete fix-suggesties',
        'Schema.org structured data (LocalBusiness, FAQ, Article, Product)',
        'Concurrenten-monitoring: welke zoekwoorden waar u op achterloopt',
        'Keyword-ranking tracking voor uw top-50 zoekwoorden',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Onze cms_executor pusht mutaties direct via REST API voor WordPress (Application Password), Shopify (Admin API), Webflow, Magento, PrestaShop en BigCommerce. Voor Wix, Squarespace, Webador, JouwWeb en Site123 genereert AI provider-specifieke snippets (HTML/JS) met exact plak-instructies — u plakt in ~30 seconden. Voor Joomla, Drupal, TYPO3, Mijndomein, Hostnet, Combell en Versio plaatst ons ops-team handmatig binnen 5 werkdagen.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Top-10 SEO-issues + groei-score', price: '€0' },
      { label: 'Self-Service', desc: 'Alle issues + AI-suggesties (Huidig → Beter) + stap-voor-stap', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'AI voert fixes ECHT uit + maandelijks rapport + ops-support', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Hoe snel zie ik resultaat van AI SEO?',
        a: 'Technische fixes (titles, meta, schema) zijn direct meetbaar in Google Search Console binnen 1-2 weken. Content-rankings bewegen typisch tussen 4-12 weken, afhankelijk van concurrentie. Backlinks-effect: 8-16 weken.',
      },
      {
        q: 'Moet ik mijn WordPress wachtwoord delen?',
        a: 'Nee. Wij gebruiken een Application Password — een aparte token die u zelf in WordPress aanmaakt (Gebruikers → Uw profiel → Wachtwoorden voor applicaties). U kunt deze elk moment intrekken zonder uw hoofdwachtwoord aan te raken.',
      },
      {
        q: 'Werkt AI SEO ook voor lokale dienstverleners?',
        a: 'Ja — sterker nog: lokaal SEO is een van onze sterke punten. Combineer AI SEO met Google Bedrijfsprofiel-beheer en lokale citations voor maximale lokale ranking. Zie /lokale-vindbaarheid.',
      },
      {
        q: 'Wat als AI iets fouts doet op mijn site?',
        a: 'Drie veiligheidslagen: (1) elke wijziging krijgt een backup, (2) u keurt strategisch goed (geen per-fix-approval-fatigue, wel categorie-goedkeuring), (3) elke actie is binnen 1 klik terug te draaien in uw dashboard.',
      },
      {
        q: 'Hoeveel kost AI SEO bij IMPERIA AI?',
        a: 'Gratis Scan: €0. Connected Self-Service: €14,95 p/m excl. btw. Managed AI Bot: €995,95 excl. btw eerste maand, daarna €2.495,95 p/m excl. btw. Alle tiers maandelijks opzegbaar.',
      },
    ],
    relatedSlugs: ['google-bedrijfsprofiel', 'lokale-vindbaarheid', 'content-strategie'],
  },

  'ai-google-ads': {
    slug: 'ai-google-ads',
    iconName: 'Target',
    accent: '#D4A017',
    h1: 'AI Google Ads — slimmer adverteren met dagelijkse optimalisatie',
    seoIntent: 'advertenties slimmer laten draaien',
    metaTitle: 'AI Google Ads Bureau · Slimmere SEA-campagnes Nederland',
    metaDescription: 'AI-beheerde Google Ads campagnes met dagelijkse optimalisatie op conversies, niet op klikken. Direct verbonden via Google Ads API.',
    keywords: 'AI Google Ads, SEA bureau, Google Ads beheer, advertentiebureau, performance marketing, Google Ads automatisch, ROAS optimalisatie',
    hero: {
      tagline: 'Google Ads API · Dagelijkse AI-optimalisatie · ROAS-tracking',
      intro:
        'Google Ads is een zwart gat als u niet weet waarop u stuurt. Onze AI is direct verbonden via de Google Ads API en optimaliseert dagelijks op zoekintenties die converteren — niet op klikken die niets opleveren. U keurt strategische wijzigingen goed; AI doet de dagelijkse fine-tuning.',
    },
    body: {
      whyTitle: 'Waarom AI Google Ads geld bespaart',
      whyBlocks: [
        {
          title: 'Geen verspilling op irrelevant verkeer',
          desc: 'AI voegt continu negatieve zoekwoorden toe, herkent zoekintentie en pauzeert ads die wel klikken maar niet converteren.',
        },
        {
          title: 'Bid-strategie per zoekterm',
          desc: 'Geen vlakke CPC. AI past biedingen aan per zoekterm op basis van conversie-data — hogere bids voor commerciële intent, lagere voor exploratie.',
        },
        {
          title: 'Cross-channel attributie',
          desc: 'Multi-touch attribution: AI ziet of een klik via Google leidde tot een conversie via een latere Meta-impressie. Geen ROI-vertekening.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'Kwaliteit-score-monitoring per advertentie en zoekwoord',
        'Automatische negatieve zoekwoorden (slimme matching op intentie)',
        'Bid-strategie per zoekterm gebaseerd op ROAS',
        'Cross-channel conversie-attributie (Google + Meta + organisch)',
        'Wekelijks AI-rapport met aanbevelingen',
        'A/B-testing van ad copy en landingspages',
        'Campagne-templates voor nieuwe productlanceringen',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Direct verbonden via Google Ads API met uw eigen ad-account (geen MCC-lock-in — uw account, uw data). AI past dagelijks bids, negatives en budget-allocatie aan. U keurt strategische wijzigingen goed (campagne-launch, budget-verhoging boven gestelde drempel, nieuw product). Maandelijks rapport met cross-channel ROAS-overzicht.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Audit van uw huidige Google Ads account (verspillings-zoektermen + KS-issues)', price: '€0' },
      { label: 'Self-Service', desc: 'AI-templates + handmatige uitvoer door u', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'Volledig beheer + dagelijkse optimalisatie + maandelijks rapport', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Heb ik al een Google Ads account nodig?',
        a: 'Nee. Wij kunnen voor u een nieuw account opzetten met alle conversie-tracking, GA4-linking en pixel-installatie. Bestaand account? Wij koppelen via Google Ads OAuth — u blijft eigenaar.',
      },
      {
        q: 'Wat is een redelijk advertentiebudget?',
        a: 'Voor lokale dienstverleners adviseren wij minimaal €750-€1.500 p/m ad-spend (apart van ons fee). Voor e-commerce typisch €1.500-€5.000 p/m. Lagere budgets kunnen, maar leveren minder AI-leerdata op.',
      },
      {
        q: 'Doen jullie ook Bing Ads, LinkedIn Ads?',
        a: 'Bing Ads (Microsoft Advertising): op aanvraag via Managed AI. LinkedIn Ads: alleen voor B2B-klanten, op aanvraag. Standaard scope: Google Ads + Meta + TikTok + Snapchat.',
      },
      {
        q: 'Krijg ik inzage in alle wijzigingen die AI doet?',
        a: 'Ja. Elke wijziging (bid-update, negative-toevoeging, ad-pause) wordt gelogd in uw dashboard met timestamp, reden en evidence-link. Volledige transparantie.',
      },
      {
        q: 'Kan ik op elk moment de AI uitschakelen?',
        a: 'Ja. Bij Managed AI staat de bot op "auto" maar u kunt elke dienst individueel pauzeren via uw dashboard. Bij Self-Service bent u sowieso zelf de uitvoerder.',
      },
    ],
    relatedSlugs: ['meta-tiktok-snapchat-ads', 'conversie-optimalisatie', 'google-analytics-dashboard'],
  },

  'meta-tiktok-snapchat-ads': {
    slug: 'meta-tiktok-snapchat-ads',
    iconName: 'Globe',
    accent: '#D4A017',
    h1: 'Social Media Ads — Meta, TikTok & Snapchat slimmer adverteren',
    seoIntent: 'social media advertenties beheren',
    metaTitle: 'Meta · TikTok · Snapchat Ads Bureau · AI-beheer Nederland',
    metaDescription: 'AI-beheerde Facebook, Instagram, TikTok en Snapchat Ads. Look-a-like audiences, A/B-testing, UGC-creatief templates, pixel + Conversies API.',
    keywords: 'Meta Ads, TikTok Ads, Snapchat Ads, social media adverteren, Facebook Ads bureau, Instagram Ads, social media advertising MKB, UGC creatief',
    hero: {
      tagline: 'Meta Pixel · Conversies API · UGC-templates · Cross-channel ROAS',
      intro:
        'Bereik klanten waar ze tijd doorbrengen. AI doet creatief-testing, audience-optimalisatie en biedmanagement op Meta, TikTok en Snapchat. Ons creatieve team levert de UGC-scripts en thumbnails; de bot test wat werkt.',
    },
    body: {
      whyTitle: 'Waarom social ads werken voor MKB',
      whyBlocks: [
        {
          title: 'Look-a-like + retargeting',
          desc: 'AI bouwt look-a-like audiences op basis van uw klantdata + retargeting-lijsten op website-bezoekers. Veel hogere conversie dan koud verkeer.',
        },
        {
          title: 'Creatief-cyclus',
          desc: 'Wij leveren elke maand nieuwe UGC-scripts + creatieve templates. AI test welke combinaties van hook + body + CTA scoren.',
        },
        {
          title: 'Conversies API + Pixel',
          desc: 'iOS 14+ tracking-loss compenseren we met de Meta Conversies API rechtstreeks vanaf uw server. Geen 30% data-verlies.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'Look-a-like audiences op basis van uw klantenbestand',
        'UGC-creatief templates (script + scene-structuur + hooks)',
        'A/B-testing per platform (Meta vs TikTok vs Snapchat performance)',
        'Cross-channel ROAS-tracking inclusief organisch effect',
        'Pixel-implementatie + Conversies API (geserveerd via uw server)',
        'Maandelijkse creatief-refresh (geen ad-fatigue)',
        'Negative audience-management (uitsluiten converters, irrelevante interests)',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Meta Pixel + Conversies API via uw bestaande GTM-container of via WordPress/Shopify plugin (auto-push). Voor TikTok en Snapchat installeren wij de pixels via dezelfde flow. AI beheert biedingen en audience-rotatie; ons creatieve team levert maandelijks 4-8 nieuwe creatieven (scripts + templates) die u of een UGC-creator filmt.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Audit huidige Meta/TikTok-account + pixel-check', price: '€0' },
      { label: 'Self-Service', desc: 'Creatieve templates + handmatig advertentie-beheer', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'Volledig beheer + creatief-refresh + ROAS-rapport', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Hebben jullie ervaring met B2B social ads?',
        a: 'Ja, met name LinkedIn Ads en Meta B2B-targeting (job-titles + interests). B2B-funnels duren typisch langer en vereisen meer content-nurturing — wij combineren ads met content + email-funnel.',
      },
      {
        q: 'Wie filmt de UGC-content?',
        a: 'U, een UGC-creator uit ons netwerk, of een freelance videograaf. Wij leveren scripts, scene-instructies en thumbnail-richtlijnen. Productie zelf valt buiten ons fee.',
      },
      {
        q: 'Werkt dit ook voor lokale fysieke winkels?',
        a: 'Absoluut. Geo-targeting binnen 5-15 km + retargeting op winkelbezoekers (via store-visit conversion + Wi-Fi-marketing). Vaak bereiken we 3-5x ROAS in eerste 3 maanden.',
      },
      {
        q: 'Hoeveel ad-spend is minimaal nodig?',
        a: 'Meta: minimaal €25-€50 per dag per campagne. TikTok: vergelijkbaar. Snapchat: vanaf €20 per dag. Voor productieve AI-leerdata adviseren wij minstens €1.500 p/m totaal ad-spend.',
      },
      {
        q: 'Mag ik mijn bestaande Meta Business Manager houden?',
        a: 'Ja, sterker nog: wij eisen dat. U bent eigenaar van uw BM en ad-account. Wij worden toegevoegd als Admin/Partner via Business Manager.',
      },
    ],
    relatedSlugs: ['ai-google-ads', 'conversie-optimalisatie', 'google-analytics-dashboard'],
  },

  'google-bedrijfsprofiel': {
    slug: 'google-bedrijfsprofiel',
    iconName: 'MapPin',
    accent: '#D4A017',
    h1: 'Google Bedrijfsprofiel beheer — domineer Google Maps',
    seoIntent: 'lokale vindbaarheid verbeteren',
    metaTitle: 'Google Bedrijfsprofiel beheer · GBP optimalisatie Nederland',
    metaDescription: 'Volledig geoptimaliseerd Google Bedrijfsprofiel met wekelijkse Local Posts, foto-management, review-respons door AI en Q&A monitoring.',
    keywords: 'Google Bedrijfsprofiel, GBP optimalisatie, Google Maps SEO, lokale vindbaarheid Google, Google My Business, GBP beheer bureau',
    hero: {
      tagline: 'Google Business Profile API · Wekelijkse Local Posts · Review AI',
      intro:
        'Voor lokale dienstverleners is Google Maps de eerste klikbron — vaak vóór uw eigen website. Een volledig geoptimaliseerd Bedrijfsprofiel met wekelijkse Local Posts, snelle review-respons en perfecte Q&A verdubbelt uw lokale ranking.',
    },
    body: {
      whyTitle: 'Waarom GBP-optimalisatie cruciaal is',
      whyBlocks: [
        {
          title: '46% van zoekopdrachten is lokaal',
          desc: 'Voor lokale diensten (loodgieter, kapper, restaurant) komt 70% van het verkeer via Google Maps + lokale 3-pack. Uw website is dan al stap 2.',
        },
        {
          title: 'Review-respons heeft ranking-effect',
          desc: 'Google ziet response-rate als kwaliteitssignaal. AI reageert binnen 24u op alle reviews, met persoonlijke tone-of-voice afgestemd op uw branche.',
        },
        {
          title: 'Local Posts geven extra zichtbaarheid',
          desc: 'Wekelijkse posts over events, aanbiedingen of nieuws verschijnen in Maps + uw GBP-card. Extra klik-mogelijkheid + signal aan Google.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'Volledige profiel-audit (categorieën, beschrijving, foto\'s, contact)',
        'Optimale primaire + secundaire categorieën (vaak vergeten — grote winst)',
        'Uitgebreide beschrijving met lokale zoekwoorden',
        'Wekelijkse Local Posts (events, aanbiedingen, nieuws, FAQ)',
        'Foto-management (toevoegen, taggen, geo-locatie)',
        'Q&A monitoring + proactieve antwoorden op vaak-gestelde vragen',
        'Review-respons door AI binnen 24u (gepersonaliseerde tone)',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Direct via Google Business Profile API (OAuth-koppeling) — u geeft één keer permissie, wij beheren. AI publiceert wekelijkse posts (geconfigureerd op uw branche + event-kalender). Review-respons via Claude Sonnet met uw merk-tone als template. U kunt elke respons goedkeuren voor publicatie of "auto-approve" instellen voor positieve reviews.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'GBP-audit + top-10 missing optimalisaties', price: '€0' },
      { label: 'Self-Service', desc: 'Optimalisatie-tips + post-templates', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'Volledig beheer + wekelijkse posts + AI review-respons', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Mijn profiel is al geclaimd — kan dat nog optimaler?',
        a: 'Bijna altijd ja. 80%+ van geclaimde profielen mist secundaire categorieën, heeft een te korte beschrijving, geen recente foto\'s en geen Local Posts. Onze gratis scan toont u direct waar de gaten zitten.',
      },
      {
        q: 'Wat als ik nog geen Google Bedrijfsprofiel heb?',
        a: 'Wij claimen het voor u en voeren de verificatie (postcaard/video/instant) uit. Daarna direct in beheer-modus.',
      },
      {
        q: 'Reageert AI ook op negatieve reviews?',
        a: 'Ja. AI gebruikt een conflict-de-escalation template + escaleert naar u voor goedkeuring vóór publicatie. Negatieve reviews krijgen nooit auto-publicatie.',
      },
      {
        q: 'Hebben jullie ervaring met multi-location?',
        a: 'Ja — vanaf 3+ locaties bieden wij een dashboard-overview en bulk-actions. Op aanvraag in Managed AI tier.',
      },
      {
        q: 'Hoeveel posts per week is genoeg?',
        a: '1-2 posts per week is optimaal. Meer voegt geen ranking-waarde toe en kan zelfs als spam ervaren worden. Wij houden de cadans bewust kalm + relevant.',
      },
    ],
    relatedSlugs: ['lokale-vindbaarheid', 'ai-seo', 'backlinks-laten-bouwen'],
  },

  'lokale-vindbaarheid': {
    slug: 'lokale-vindbaarheid',
    iconName: 'ShieldCheck',
    accent: '#D4A017',
    h1: 'Lokale vindbaarheid — lokale klanten aantrekken via Google',
    seoIntent: 'lokale klanten aantrekken',
    metaTitle: 'Lokale SEO Bureau · Lokale klanten aantrekken via Google',
    metaDescription: 'Combinatie van Google Bedrijfsprofiel, lokale citations, branche-listings en geo-targeted ads. Domineer uw verzorgingsgebied.',
    keywords: 'lokale SEO, lokale vindbaarheid, lokale klanten aantrekken, citation building, NAW-consistentie, lokale advertenties, geo-targeting',
    hero: {
      tagline: 'GBP + citations + geo-ads + lokale content — alles geïntegreerd',
      intro:
        'Voor zaken met een verzorgingsgebied (loodgieter, advocaat, restaurant, kapper) is lokale vindbaarheid de belangrijkste groei-hefboom. Wij combineren GBP-beheer, citation-consistentie op 50+ directories, geo-targeted ads en lokale landingspages tot één geïntegreerd plan.',
    },
    body: {
      whyTitle: 'Waarom lokale vindbaarheid winst maakt',
      whyBlocks: [
        {
          title: 'Hoge intentie, lage concurrentie',
          desc: '"Loodgieter Zwolle" heeft veel minder concurrentie dan "loodgieter NL" — én veel hogere koop-intentie. Hier scoort u écht.',
        },
        {
          title: 'Citation-consistentie als ranking-factor',
          desc: 'Google checkt of uw NAW (naam, adres, telefoon) overal hetzelfde is. Inconsistentie = ranking-penalty. Wij houden 50+ directories sync.',
        },
        {
          title: 'Geo-ads met lage CPC',
          desc: 'Lokale Google + Meta ads met 5-15 km radius hebben typisch 3-5x lagere CPC dan landelijk. Veel betere ROI.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'Citation-audit op 50+ Nederlandse directories (NAW-consistentie check)',
        'Lokale landingspages per stad/regio (waar u actief bent)',
        'Geo-targeted Google + Meta Ads (5-15 km radius + uitsluitingen)',
        'Buurt-content (markten, evenementen, lokale partnerships)',
        'GBP-optimalisatie (zie /google-bedrijfsprofiel)',
        'Lokale schema.org structured data (LocalBusiness, OpeningHours)',
        'Concurrentie-monitoring per stad',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'AI scant uw NAW op 50+ directories en flagt inconsistenties. Ons ops-team claimt ontbrekende profielen en corrigeert verkeerde vermeldingen. Lokale landingspages worden via cms_executor of snippet-mode gepubliceerd. Geo-ads draaien via uw Google Ads + Meta Business Manager (u blijft eigenaar).',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Citation-audit + lokale rank-check', price: '€0' },
      { label: 'Self-Service', desc: 'Audit + listing-templates + landing-templates', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'Volledige uitvoering + claim-werk + geo-ads beheer', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Werkt dit ook voor service-area-bedrijven (zonder fysiek adres)?',
        a: 'Ja. GBP heeft een "service area" optie waarmee u uw verzorgingsgebied aangeeft zonder een publiek adres te tonen. Wij configureren dit correct.',
      },
      {
        q: 'Moet ik per stad een aparte landingspagina?',
        a: 'Voor 2-5 belangrijkste steden: ja (sterke ranking-boost). Voor 20+ steden: één hoofdpagina + dynamic content. We adviseren op basis van uw verzorgingsgebied.',
      },
      {
        q: 'Wat is "citation-consistentie" precies?',
        a: 'NAW (Naam, Adres, Woonplaats) inclusief telefoon en website moeten over ALLE online vermeldingen exact hetzelfde geschreven zijn. Verschil tussen "Hoofdstraat 1" en "Hoofdstraat 1A" telt al.',
      },
      {
        q: 'Krijg ik echte ranking-rapporten?',
        a: 'Ja — wekelijkse local rank-tracker met top-30 zoekwoorden per stad. In uw dashboard zichtbaar + maandelijks PDF.',
      },
    ],
    relatedSlugs: ['google-bedrijfsprofiel', 'ai-seo', 'backlinks-laten-bouwen'],
  },

  'backlinks-laten-bouwen': {
    slug: 'backlinks-laten-bouwen',
    iconName: 'Link2',
    accent: '#D4A017',
    h1: 'Backlinks laten bouwen — branche-specifiek, geen spam',
    seoIntent: 'autoriteit en backlinks bouwen',
    metaTitle: 'Backlinks Laten Bouwen · 80+ NL Directories + Branche-specifiek',
    metaDescription: '80+ Nederlandse directories in catalog. AI selecteert branche-specifieke en hoog-DA listings. 25-40 nieuwe backlinks per maand bij Managed AI.',
    keywords: 'backlinks laten bouwen, link building bureau, NL directories, Werkspot backlink, Iens backlink, autoriteit bouwen, domain authority',
    hero: {
      tagline: '80+ NL directories · 25-40 nieuwe backlinks/maand · 46+ branche-specifiek',
      intro:
        'Backlinks blijven dé sterkste ranking-factor — maar massa-outreach naar dubieuze sites schaadt meer dan het oplevert. Wij hebben 80+ Nederlandse directories in onze catalog, waarvan 46+ branche-specifiek. AI selecteert hoog-DA en branche-relevante listings — geen spam.',
    },
    body: {
      whyTitle: 'Waarom branche-specifiek werkt beter',
      whyBlocks: [
        {
          title: '46+ branche-directories',
          desc: 'Restaurants: Iens, TheFork, Eet.nu. Bouw: Werkspot, HomeDeal, Bouwwereld. Zorg: ZorgkaartNL, Independer. Auto: AutoScout24, AutoTrack. Branche-specifiek = relevanter = hoger ranking-effect.',
        },
        {
          title: 'Domain Authority transparant',
          desc: 'Per directory tonen wij de Moz Domain Authority. Wij prioriteren DA 40+ — geen rommelige farms met DA 12.',
        },
        {
          title: 'Geen massa-outreach',
          desc: 'Kwaliteit boven kwantiteit. 25-40 sterke backlinks per maand beats 200 spam-links die Google later afstraft.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        '35 algemene NL/EU directories (KvK, Telefoonboek, Cylex, Yelp NL, Gouden Gids, Europages, Bing Places, Apple Business Connect)',
        '46+ branche-specifiek over 7 branches (bouw, horeca, zorg, retail, automotive, dienstverlening, onderwijs)',
        'Reviewplatforms (Trustpilot, Kiyoh, Klantenvertellen, Feedback Company)',
        'Outreach naar relevante NL blogs (gastblog-pitches)',
        'NAW-consistentie monitoring (geen valse vermeldingen)',
        'Maandelijks 25-40 nieuwe backlinks (Managed AI tier)',
        'Domain Authority + rejection-monitoring',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'AI doet de outreach via onze email_outbox (paused-aware queue). Wij claimen profielen, vullen branche-specifieke velden in en monitoren rejections. Voor reviewplatforms zetten wij review-flow op uw site op. Outreach naar blogs gaat via gepersonaliseerde pitches met uw expert-content als haakje.',
    },
    tiers: [
      { label: 'Free Scan', desc: '3 backlink-kansen voor uw branche', price: '€0' },
      { label: 'Self-Service', desc: 'Outreach-templates + DA-overzicht 80+', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: '25-40 nieuwe backlinks/maand + claim-werk + monitoring', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Hoe weet ik dat de backlinks niet "spammy" zijn?',
        a: 'Per directory tonen wij de Moz Domain Authority + branche-fit-score. Minimum DA 25, gemiddeld DA 45+. Voor reviewplatforms (DA 70+) altijd top-prio.',
      },
      {
        q: 'Krijg ik echt 25-40 backlinks per maand?',
        a: 'Bij Managed AI: ja, gegarandeerd. In maand 1 vaak 40+ (laagdrempelige listings). Vanaf maand 3+ stabiliseert dit op 25-30/maand. Op /prijzen ziet u de live preview-widget met directory-aantallen per branche.',
      },
      {
        q: 'Moet ik per directory zelf invullen?',
        a: 'Nee. Wij claimen profielen met uw NAW + KvK-info. U keurt strategische velden goed (beschrijving, hoofdcategorie). Wij beheren de rest.',
      },
      {
        q: 'Wat als een directory ons afwijst?',
        a: 'Wij loggen de reden, optimaliseren de aanvraag (vaak: betere beschrijving, juiste categorie, foto\'s) en proberen opnieuw. 90%+ wordt uiteindelijk geaccepteerd.',
      },
    ],
    relatedSlugs: ['ai-seo', 'lokale-vindbaarheid', 'google-bedrijfsprofiel'],
  },

  'website-optimalisatie': {
    slug: 'website-optimalisatie',
    iconName: 'Wrench',
    accent: '#D4A017',
    h1: 'Website optimalisatie — continue verbetering, geen redesign',
    seoIntent: 'website automatisch verbeteren',
    metaTitle: 'Website Optimalisatie Bureau · Snelheid + Conversie + UX',
    metaDescription: 'Continue mutaties op uw bestaande site: Core Web Vitals, CTA-tweaks, formulier-optimalisatie, mobile-first. Auto-push voor WordPress/Shopify.',
    keywords: 'website optimalisatie, Core Web Vitals, PageSpeed verbeteren, conversie optimalisatie, mobile-first, CTA optimaliseren, formulier optimalisatie',
    hero: {
      tagline: 'Core Web Vitals · CTA-tweaks · Mobile-first · Auto-push',
      intro:
        'Uw website hoeft niet opnieuw — wel slimmer. Continue mutaties op snelheid, UX, conversie-elementen en mobile-friendliness houden uw site fit. Geen 6-weken-redesign-project; wel maandelijks meetbaar betere resultaten.',
    },
    body: {
      whyTitle: 'Waarom continue optimalisatie beats redesign',
      whyBlocks: [
        {
          title: 'Geen project-cyclus',
          desc: 'Redesigns kosten 3-6 maanden. In die tijd verliest u traffic. Continue optimalisatie levert wekelijks micro-wins op zonder downtime.',
        },
        {
          title: 'Mobile-first',
          desc: '70%+ van uw verkeer is mobiel. AI checkt elke wijziging op mobile breakpoints en flagt issues vóór publicatie.',
        },
        {
          title: 'A/B-testen ingebouwd',
          desc: 'Voor CTA-wijzigingen draaien we automatisch A/B-tests via Google Optimize Next of een lichte cookie-flag. Data-driven, niet "wij denken dat groen werkt".',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'Core Web Vitals monitoring (LCP, INP, CLS) + concrete fixes',
        'Above-fold optimalisatie (hero + CTA + trust-signals)',
        'Formulier-optimalisatie (veld voor veld — schrappen wat afhaakt)',
        'Mobile-first responsiveness checks',
        'Image-compressie + lazy loading',
        'CTA-positionering en copy-tests',
        'Speed-budget per pagina (kb-limieten enforced)',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Auto-push voor WordPress (via REST API), Shopify (Admin API), Webflow, Magento, PrestaShop, BigCommerce. Snippets voor Wix/Squarespace/Webador/JouwWeb. Ops-team voor Joomla/Drupal/TYPO3 (SLA 5 werkdagen). Image-compressie + WebP-conversie automatisch via Cloudinary of equivalent.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Speed-test + top-10 quick wins', price: '€0' },
      { label: 'Self-Service', desc: 'Tip-lijst + stap-voor-stap per fix', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'AI voert fixes ECHT uit + maandelijkse iteraties', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Wat als mijn site al "snel" is?',
        a: 'Snel volgens wie? Volgens PageSpeed Insights of volgens uw bezoekers? Onze gratis scan toont real-user metrics (CrUX-data). Vaak blijken er nog 20-40% winst-marges, vooral op mobile.',
      },
      {
        q: 'Kan ik dit doen zonder redesign?',
        a: 'Ja — daar zit juist de kracht. 80%+ van conversie-winst komt van micro-optimalisaties (CTA-tekst, formulier-velden, hero-headline) — niet van een visuele overhaul.',
      },
      {
        q: 'Wat als ik wél een redesign wil?',
        a: 'Zie /website-redesign — onze redesign-flow is conversie-first, geen design-first. Inbegrepen in Managed AI.',
      },
    ],
    relatedSlugs: ['website-redesign', 'conversie-optimalisatie', 'nieuwe-website-laten-maken'],
  },

  'website-redesign': {
    slug: 'website-redesign',
    iconName: 'Crown',
    accent: '#D4A017',
    h1: 'Website redesign — conversie-eerst, niet design-first',
    seoIntent: 'website laten vernieuwen',
    metaTitle: 'Website Redesign Bureau · Conversie-first redesign Nederland',
    metaDescription: 'Strategisch redesign van uw bestaande website. User-flow audit, hero + structuur rewrite, A/B-test launch. Live binnen 4-6 weken. Inbegrepen in Managed AI.',
    keywords: 'website redesign, website laten vernieuwen, conversie redesign, website rewrite, CMS migratie, WordPress redesign, A/B-test launch',
    hero: {
      tagline: 'User-flow audit · Hero + structuur rewrite · A/B-test launch',
      intro:
        'Een redesign is geen visuele upgrade — het is een conversie-strategie. Wij beginnen met data (waar haakt bezoeker af, welke pagina\'s converteren niet) en bouwen van daaruit. Live binnen 4-6 weken, zonder SEO-verlies.',
    },
    body: {
      whyTitle: 'Wanneer een redesign écht zin heeft',
      whyBlocks: [
        {
          title: 'Conversie-percentage onder 1,5%',
          desc: 'Voor lead-gen sites is <1,5% een teken dat structuur of UX in de weg zit. Bij e-commerce <0,8%. Dan loont een redesign.',
        },
        {
          title: 'Verouderd CMS',
          desc: 'Joomla 1.x, oude Magento 1.x, of WordPress < 6.0 met 50+ plugins. CMS-migratie is dan onderdeel van redesign.',
        },
        {
          title: 'Multi-merk of nieuwe propositie',
          desc: 'Bedrijf gegroeid, propositie verbreed of nieuwe markten? Vaak past de oude site-structuur niet meer.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'User-flow audit (waar haakt bezoeker af) op basis van GA4 + heatmaps',
        'Hero + structuur-rewrite (waar nodig per pagina)',
        'CMS-migratie indien nodig (WordPress, Shopify of headless)',
        'A/B-test launch (oude vs nieuwe — data-driven keuze)',
        'Live migratie zonder SEO-verlies (301-redirects, canonicals)',
        'Schema.org compleet (Organization, BreadcrumbList, FAQ, Article)',
        '4-6 weken doorlooptijd vanaf goedkeuring',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Strategie + design door ons team, implementatie door AI bot via CMS-API. U keurt de design-mockup goed, daarna voert AI de structurele wijzigingen door via WordPress REST API of Shopify Admin. Voor CMS-migratie zetten wij een nieuwe omgeving op, migreren content en switchen DNS na uw go-ahead.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Redesign-audit + concrete aanbevelingen', price: '€0' },
      { label: 'Self-Service', desc: 'Niet beschikbaar — redesign valt onder Managed', price: '—' },
      { label: 'Managed AI', desc: 'Volledig redesign + CMS-migratie + launch inbegrepen', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Verlies ik mijn SEO-positie bij redesign?',
        a: 'Niet als het correct gebeurt. Wij maken een complete URL-mapping (oud → nieuw) en zetten 301-redirects op. Schema.org wordt verbeterd, niet verminderd. Doorlooptijd: typisch 4-6 weken zonder rank-verlies.',
      },
      {
        q: 'Wordt de redesign visueel ook beter?',
        a: 'Ja — maar visueel design volgt conversie-strategie, niet andersom. Geen "fancy hero met grote video" als dat de mobile load-time vertraagt.',
      },
      {
        q: 'Kan ik mijn huidige content behouden?',
        a: 'Ja, voor 70-90% wel. Vaak rewriten wij hoofdpagina\'s (homepage, dienst-pagina\'s) en behouden blog-archief 1-op-1.',
      },
      {
        q: 'Wat als ik halverwege wil stoppen?',
        a: 'Bij Managed AI is geen jaar-lock-in. Wij leveren wat tot dan toe gemaakt is + URL-mapping. U gaat verder met een ander bureau zonder data-verlies.',
      },
    ],
    relatedSlugs: ['nieuwe-website-laten-maken', 'website-optimalisatie', 'conversie-optimalisatie'],
  },

  'nieuwe-website-laten-maken': {
    slug: 'nieuwe-website-laten-maken',
    iconName: 'Sparkles',
    accent: '#D4A017',
    h1: 'Nieuwe website laten maken — AI-eerst + handmatige finetune',
    seoIntent: 'nieuwe website laten maken',
    metaTitle: 'Nieuwe Website Laten Maken · AI-website binnen 14 dagen',
    metaDescription: 'Voor ondernemers zonder website of met een verouderde site: AI-gegenereerde basis + handmatige finetune door ons team. Live binnen 14 dagen.',
    keywords: 'nieuwe website laten maken, website laten bouwen, AI website maker, WordPress website laten maken, Shopify webshop laten maken',
    hero: {
      tagline: 'AI content + structuur · Live in 14 dagen · WordPress of Shopify',
      intro:
        'Geen website? Of een site uit 2014 die niet meer past? Wij genereren met AI een eerste volledig SEO-geoptimaliseerde versie binnen 48 uur, ons team finetuned, u keurt goed, wij publiceren. Live binnen 14 dagen.',
    },
    body: {
      whyTitle: 'Waarom AI-website + handmatige finetune?',
      whyBlocks: [
        {
          title: 'Snel zonder concessies',
          desc: 'AI doet het zware werk (content, structuur, SEO-fundament). Wij finetunen. Resultaat: kwaliteit van een agency-build, in een fractie van de tijd.',
        },
        {
          title: 'SEO vanaf launch',
          desc: 'Schema.org, sitemap, robots.txt, canonicals, Open Graph — allemaal correct vanaf dag 1. Geen "we doen de SEO later".',
        },
        {
          title: 'Hosting + domeinen geregeld',
          desc: 'Wij regelen hosting (managed WordPress of Shopify), domein-koppeling, SSL en e-mail-MX records. U hoeft niets te configureren.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'AI content + structuur op basis van uw branche + propositie',
        'CMS keuze (WordPress voor content-sites, Shopify voor e-commerce)',
        'Hosting + domeinen geregeld (managed WordPress of Shopify Basic)',
        'SEO-optimaal vanaf launch (schema, sitemap, OG, GA4)',
        'Mobile-first responsive (geen apart mobile-design nodig)',
        'Cookie-banner + AVG-compliance',
        'Live binnen 14 dagen vanaf goedkeuring',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'AI levert eerste versie binnen 48u (content + structuur via Claude Sonnet). Ons design-team finetuned hero, kleuren, typografie. U keurt goed in een interactive preview-omgeving. Daarna publiceren wij via WordPress REST API of Shopify Admin. Hosting via Cloudways/Kinsta (WordPress) of native Shopify Basic plan.',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Niet beschikbaar — u heeft eerst een site nodig om te scannen', price: '—' },
      { label: 'Self-Service', desc: 'Niet beschikbaar — nieuwe websites vallen onder Managed', price: '—' },
      { label: 'Managed AI', desc: 'Volledige website + hosting + launch inbegrepen', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Hoe kan een AI-website "live in 14 dagen"?',
        a: 'AI doet het tijd-intensieve content-werk (10-15 pagina\'s schrijven). Ons team finetuned, u keurt goed in 2-3 review-rondes. Geen 6-weken-discovery-fase — wij weten al wat een MKB-site nodig heeft.',
      },
      {
        q: 'Krijg ik echt SEO-content of een template?',
        a: 'Echte content gebaseerd op uw branche, uw diensten en lokale keywords. AI genereert een eerste versie + ons team rewrites onderscheidende secties. Geen templates met "ABC Bedrijf" placeholder-tekst.',
      },
      {
        q: 'Kan ik later switchen van WordPress naar Shopify?',
        a: 'Ja — onder Managed AI is migratie inbegrepen. Geen vendor-lock-in. URL-mapping + 301-redirects worden meegenomen.',
      },
      {
        q: 'Hosting — wie is eigenaar?',
        a: 'U. Wij regelen het, maar u staat als account-houder ingeschreven. Bij eventuele opzegging neemt u uw hosting + domein mee.',
      },
    ],
    relatedSlugs: ['website-redesign', 'website-optimalisatie', 'ai-seo'],
  },

  'conversie-optimalisatie': {
    slug: 'conversie-optimalisatie',
    iconName: 'TrendingUp',
    accent: '#D4A017',
    h1: 'Conversie-optimalisatie (CRO) — meer klanten zonder meer verkeer',
    seoIntent: 'meer klanten uit mijn website',
    metaTitle: 'Conversie-Optimalisatie Bureau · CRO Audit + A/B-Testing',
    metaDescription: 'AI-analyse van uitvallers, heatmaps, formulier-completion. Iteratieve A/B-tests op hero, CTAs en formulieren. Maandelijkse meetbare lift.',
    keywords: 'conversie optimalisatie, CRO bureau, A/B-test bureau, formulier optimalisatie, heatmap analyse, conversie verhogen, meer klanten website',
    hero: {
      tagline: 'Microsoft Clarity + AI-analyse · A/B-tests · Maandelijkse iteraties',
      intro:
        'Meer verkeer betekent niet automatisch meer klanten. CRO (Conversion Rate Optimization) richt zich op de bezoekers die u al heeft — en haalt daar meer uit. AI analyseert heatmaps, scroll-depth en formulier-completion, en stelt iteratieve verbeteringen voor.',
    },
    body: {
      whyTitle: 'Waarom CRO de hoogste ROI heeft',
      whyBlocks: [
        {
          title: 'Geen extra ad-spend',
          desc: 'Conversie van 1,5% → 2,5% = 67% meer klanten zonder één extra euro aan advertenties. Hier zit verborgen winst.',
        },
        {
          title: 'Data, geen meningen',
          desc: 'Heatmaps + scroll-depth + form-analytics tonen exact waar bezoekers afhaken. Geen "wij denken dat de knop groen moet".',
        },
        {
          title: 'A/B-test infrastructuur',
          desc: 'Veranderingen worden eerst A/B-getest (10-20% verkeer) voordat ze 100% live gaan. Geen omzet-risico.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'CRO-audit per pagina (homepage, dienst, contact, checkout)',
        'Heatmap + scroll-depth tracking (Microsoft Clarity — gratis)',
        'Formulier-optimalisatie (veld voor veld — schrappen wat afhaakt)',
        'CTA-positionering en copy-tests',
        'A/B-tests op hero + checkout (kwartaal-cyclus)',
        'Funnel-analyse (waar valt 60-70% uit?)',
        'Mobile-vs-desktop optimalisatie',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Microsoft Clarity-integratie via GTM of cms_executor (gratis tool — geen extra software-fees). AI-analyse van heatmaps + funnel-data. Mutaties via cms_executor of snippet-mode. A/B-tests via Google Optimize Next of een lichte cookie-flag (geen 3rd-party tool nodig).',
    },
    tiers: [
      { label: 'Free Scan', desc: 'CRO-audit van uw homepage + contact-flow', price: '€0' },
      { label: 'Self-Service', desc: 'Audit + verbeter-suggesties + heatmap-setup', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'Maandelijkse iteraties + A/B-tests + ROAS-rapport', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Hoeveel verkeer is minimaal nodig voor CRO?',
        a: 'Voor A/B-tests: minimaal 500-1.000 unieke bezoekers per maand per geteste variant. Voor lagere volumes focussen we op heatmap-driven beslissingen zonder A/B (sneller, minder data-zekerheid).',
      },
      {
        q: 'Werkt CRO ook voor B2B-sites met lange sales-cycles?',
        a: 'Ja, maar de meetbare KPI verschuift van "aankoop" naar "MQL" (marketing-qualified lead) of "demo-aanvraag". Wij configureren multi-step conversie-doelen.',
      },
      {
        q: 'Hoeveel lift kan ik verwachten?',
        a: 'In 3-6 maanden zien wij typisch 20-50% lift in conversie-percentage. Geen garantie — afhankelijk van uitgangspunt. Hoe lager de startpercentage (<1%), hoe meer winst-marge.',
      },
    ],
    relatedSlugs: ['website-optimalisatie', 'google-analytics-dashboard', 'ai-google-ads'],
  },

  'content-strategie': {
    slug: 'content-strategie',
    iconName: 'FileText',
    accent: '#D4A017',
    h1: 'Content strategie — meer organisch verkeer met AI-content',
    seoIntent: 'meer organisch verkeer',
    metaTitle: 'Content Marketing Bureau · AI Content + SEO Artikelen',
    metaDescription: 'AI schrijft, ons team redigeert. Maandelijks 4-8 SEO-geoptimaliseerde artikelen. Keyword research, topic clusters, AI-publicatie naar uw CMS.',
    keywords: 'content marketing, AI content, SEO artikelen, blog laten schrijven, topic clusters, content strategie, keyword research',
    hero: {
      tagline: 'Claude Sonnet + ops-redactie · 4-8 artikelen p/m · AI-publicatie naar CMS',
      intro:
        'Content drijft langetermijn-SEO. Maar 4-8 sterke artikelen per maand schrijven naast uw dagelijkse werk is onhaalbaar. AI schrijft de eerste versie, ons team redigeert (tone, accuracy, lokale context), en wij publiceren direct in uw CMS.',
    },
    body: {
      whyTitle: 'Waarom AI-content + redactie de juiste mix is',
      whyBlocks: [
        {
          title: 'Schaalbaar zonder kwaliteitsverlies',
          desc: 'AI alleen levert generiek werk. Mens-only is duur. Combinatie: AI doet 70% van het werk, ons team voegt expertise + lokale context toe.',
        },
        {
          title: 'Topic clusters voor maximum SEO',
          desc: 'Niet losse artikelen — we bouwen pillar-content (hoofdpagina) met 8-15 sub-artikelen die intern linken. Veel sterker voor Google.',
        },
        {
          title: 'Direct in uw CMS',
          desc: 'Geen Word-documenten over en weer mailen. AI publiceert direct naar WordPress/Shopify met featured image, categorieën, tags en schema.org markup.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'Keyword research op zoekintenties (commercial / informational / navigational)',
        'Topic clusters (1 pillar-pagina + 8-15 sub-artikelen)',
        'Maandelijks 4-8 SEO-geoptimaliseerde artikelen (700-1.500 woorden)',
        'AI-publicatie direct naar uw CMS (WordPress/Shopify/Webflow)',
        'Internal linking optimalisatie',
        'Featured images via AI (geen stock-clichés)',
        'Schema.org Article + BreadcrumbList per post',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Claude Sonnet via emergentintegrations schrijft eerste versie. Ops-redactie checkt op tone, accuracy en lokale context (Nederlandse spelling, branche-jargon, juridische correctheid). AI publiceert via cms_executor (WordPress REST API of Shopify Admin). Voor non-API CMS\'en: snippet-mode (u plakt artikel-HTML in 30 sec).',
    },
    tiers: [
      { label: 'Free Scan', desc: 'Keyword research + 3 topic-suggesties voor uw branche', price: '€0' },
      { label: 'Self-Service', desc: 'AI-suggesties + outline-templates (u schrijft zelf)', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: '4-8 volledige artikelen p/m + publicatie inbegrepen', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Detecteert Google AI-content?',
        a: 'Google\'s eigen guidance: kwaliteit telt, niet wie/wat het maakte. Onze mix (AI-draft + menselijke redactie + lokale expertise) levert echte expertise-content. Wij hebben geen ranking-issues gezien.',
      },
      {
        q: 'Wie heeft auteursrecht op de content?',
        a: 'U. Alle door ons gepubliceerde content is uw eigendom — ook bij opzegging behoudt u alles wat live staat.',
      },
      {
        q: 'Kunnen jullie ook white-papers / e-books schrijven?',
        a: 'Ja, op aanvraag in Managed AI. Diepere content (3.000+ woorden, met data + interviews) valt buiten standaard cadans en wordt apart geprijsd.',
      },
      {
        q: 'Hoe blijft de content "uw merk-tone"?',
        a: 'Bij onboarding maken wij een tone-of-voice document (formeel/casual, jij/u, jargon-gebruik, no-go woorden). AI traint daarop. Ops-redactie checkt élke draft.',
      },
    ],
    relatedSlugs: ['ai-seo', 'google-analytics-dashboard', 'conversie-optimalisatie'],
  },

  'google-analytics-dashboard': {
    slug: 'google-analytics-dashboard',
    iconName: 'BarChart3',
    accent: '#D4A017',
    h1: 'Google Analytics + dashboard — meer leads uit uw website',
    seoIntent: 'meer leads uit mijn website',
    metaTitle: 'GA4 + Custom Dashboard · Conversie-tracking voor MKB',
    metaDescription: 'GA4 + Search Console + conversie-events. Custom dashboard waarin u per dienst en kanaal ziet wat omzet oplevert.',
    keywords: 'Google Analytics, GA4 setup, conversie tracking, custom dashboard, marketing dashboard, lead tracking website, Google Tag Manager',
    hero: {
      tagline: 'GA4 + GSC + GTM · Conversie-events · Custom rapporten',
      intro:
        'Zonder meten weet u niet wat werkt. GA4 + Search Console + conversie-events op formulieren en telefoonclicks geven u real-time inzicht in welk kanaal échte leads oplevert. Custom dashboard op /platform — geen GA4-spaghetti.',
    },
    body: {
      whyTitle: 'Waarom standaard GA4 niet volstaat',
      whyBlocks: [
        {
          title: 'Default GA4 mist alles',
          desc: 'Out-of-the-box meet GA4 alleen page_views. Conversies, telefoon-clicks, formulier-submits, scroll-depth, file-downloads — allemaal handmatig configureren.',
        },
        {
          title: 'Custom dashboard, geen GA4-spaghetti',
          desc: 'Wij bouwen één dashboard waarin u per dienst (SEO, Ads, social) en per kanaal (organic, paid, direct) ziet wat omzet oplevert. Geen 47 GA4-tabs.',
        },
        {
          title: 'Wekelijkse e-mail highlights',
          desc: 'Top 3 wins + top 3 zorgen — geconfigureerd op uw KPI\'s. Geen vage "verkeer is met 7% gestegen" rapportage.',
        },
      ],
      whatTitle: 'Wat u krijgt',
      what: [
        'GA4 setup via Google Tag Manager (clean implementatie)',
        'Search Console-integratie',
        'Conversie-doelen voor formulieren, telefoonclicks, file-downloads',
        'Custom dashboard op /platform (per dienst + per kanaal)',
        'Wekelijkse e-mail highlights (top 3 wins + top 3 zorgen)',
        'Cookie-banner + AVG-compliance (Consent Mode v2)',
        'Cross-domain tracking indien nodig',
      ],
      uitvoeringTitle: 'Hoe wij dit uitvoeren',
      uitvoering:
        'Setup eenmalig via guided flow of automatisch via WordPress/Shopify integratie. Voor non-API CMS\'en: snippet-mode (u plakt GTM-tag in 30 sec). Dashboard live op /platform via Self-Service of Managed AI. Wekelijkse highlights via email_outbox (paused-aware).',
    },
    tiers: [
      { label: 'Free Scan', desc: 'GA4-audit + missing conversion-events', price: '€0' },
      { label: 'Self-Service', desc: 'Dashboard-toegang + setup-guides', price: '€14,95 p/m' },
      { label: 'Managed AI', desc: 'Volledige setup + custom rapporten + wekelijkse highlights', price: 'vanaf €995,95' },
    ],
    faq: [
      {
        q: 'Hoe zit het met AVG en cookie-toestemming?',
        a: 'Wij implementeren Google Consent Mode v2 zodat GA4 ook werkt zonder toestemming (geanonimiseerd) én met volledige tracking bij toestemming. Cookie-banner kunnen wij voor u opzetten (Cookiebot of een lichte open-source variant).',
      },
      {
        q: 'Wat als ik nog Universal Analytics gebruik?',
        a: 'Universal Analytics is sinds juli 2023 gestopt. Wij doen een gratis migratie naar GA4 inclusief historische data-export (waar mogelijk).',
      },
      {
        q: 'Krijg ik telefoon-tracking?',
        a: 'Click-to-call tracking via een click-event op uw telefoon-link: ja. Echte call-tracking (welk nummer belt naar welk advertentie-source): vereist een call-tracking-tool zoals CallRail (apart abonnement).',
      },
    ],
    relatedSlugs: ['conversie-optimalisatie', 'ai-google-ads', 'ai-seo'],
  },
};

/** Array versie voor mapping (voor route-generatie + index-pagina). */
export const SERVICE_DETAILS_LIST = Object.values(SERVICE_DETAILS);

/** Snelle lookup helper. */
export function getServiceDetail(slug) {
  return SERVICE_DETAILS[slug] || null;
}
